import React, { useState, useEffect, useRef } from "react";
import { Form, Schema, Tooltip } from "rsuite";
import styled from "styled-components";

import { Alert } from "services/Alert";

import { compareObjects, fullPhoneRegex } from "utils";
import { withTranslation } from "hoc/WithTranslation";
import { useWindowWidth } from "hooks";
import { FilterSectionFlexboxGrid, StyledItemFitWidth } from "components/base/FitContainers";
import { ButtonPrimary } from "components/base/BaseButton";
import Whisper from "components/Whisper";
import { Icon } from "components/Icon";

import ModalEditProfileContactsLetter from "./ModalEditProfileContactsLetter";
import ModalEdit from "../Modal/ModalEdit";
import m from "definedMessages";

const { StringType } = Schema.Types;

const MIN_PASSWORD_LENGTH = 8;
const MAX_CHARACTERS = 40;


const ModalEditProfileContacts = ({
    maxWidth = 870,
    show,
    onClose,
    loading,
    isOwner,
    dialerInfo,
    contactList,
    contactLoading,
    absentTeamsContact,

    getAccountDialerInfo,
    getAccountContactList,
    modifyAccountContact,
    modifyAccountContactRequest,
    setAccountContactLoadingData: setLoading,

    formatMessage,
    ...props
}) => {

    const contactsRefsData = useRef({});

    const windowWidth = useWindowWidth();

    const isMobile = windowWidth < 820;

    const [showLetterModal, setShowLetterModal] = useState(false);
    const [letterModalData, setLetterModalData] = useState({
        id: null,
        name: null,
        email: null
    });

    const [contacts, setContacts] = useState(contactList || []);
    const [blockModal, setBlockModal] = useState(false);

    useEffect(() => {
        if (isOwner) {
            setBlockModal(absentTeamsContact);
        }
    }, [isOwner, absentTeamsContact]);

    useEffect(() => {
        getAccountDialerInfo();
        getAccountContactList();
    }, []);

    useEffect(() => {
        setContacts(contactList || []);
    }, [contactList]);

    const updateRefsData = (ref, id) => {
        if (ref) {
            contactsRefsData.current[id] = ref;
        } else {
            delete contactsRefsData.current[id];
        }
    };

    const formProfileContactModel = Schema.Model({
        phone: StringType().addRule(value => fullPhoneRegex.test(value), formatMessage(m.pleaseEnterValidPhoneNumber)),
        telegram_username: StringType()
            .pattern(/.*\B@(?=\w{5,32}\b)[a-zA-Z0-9]+(?:_[a-zA-Z0-9]+)*.*/, formatMessage(m.telegramUsernameMustBe5to32long)),
        teams_email: StringType()
            .isRequired(formatMessage(m.required))
            .maxLength(256, formatMessage(m.maxLength, { count: 256 }))
            .pattern(/^([a-zA-Z0-9_+.-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/, formatMessage(m.incorrectEmailAddress)),
        teams_link: StringType()
            .isRequired(formatMessage(m.required))
            .maxLength(256, formatMessage(m.maxLength, { count: 256 }))
            .pattern(/^(https?:\/\/)([a-z0-9-]+\.)+[a-z]{2,}(:[0-9]{1,5})?(\/.*)?$/i, formatMessage(m.incorrectTeamsLink)),
    });

    const addableIsChanged = (id, addableObjectInitial, addableObject) => {
        const addedNew = addableObjectInitial && !addableObjectInitial.map((addable) => addable.id).includes(id);
        const changed = addableObjectInitial && !compareObjects(addableObjectInitial.find(obj => obj.id === id), addableObject);

        return addedNew || changed;
    };

    const onSubmit = () => {
        const changedContacts = contacts.filter(contact => (
            addableIsChanged(contact.id, contactList, contact)
        ));

        const formsAreValid = changedContacts
            .map(contact => contactsRefsData.current[contact.id]?.check() === true)
            .every(Boolean);

        if (!formsAreValid) {
            return false;
        }

        changedContacts.forEach((contact) => {
            const data = JSON.stringify({
                phone: contact?.phone,
                telegram_username: contact?.telegram_username,
                teams_email: contact?.teams_email,
                teams_link: contact?.teams_link
            }, (_, value) => {
                if (!value) {
                    return null;
                }

                return value;
            });

            const clearedData = JSON.parse(data);

            modifyAccountContact(contact.id, clearedData)
                .then((result) => {
                    if (Object.keys(result).length && !result?.error) {
                        Alert.success(formatMessage(m.contactPersonHasBeenChanged, { person: contact.person }))
                    }
                })
                .finally(() => {
                    getAccountContactList();

                    setLoading(false);

                    if (blockModal) {
                        onClose();
                    }
                });
        });

        return true;
    };

    const checkFormError = (errorObj) => {
        return Object.keys(errorObj).map(i => errorObj[i].hasError).every(value => value === false)
    };

    const updateFormValues = (formValues, id) => {
        contacts && setContacts([
            ...contacts.map(contact =>
                contact.id === id ? { ...contact, ...formValues } : contact
            )
        ]);
    };

    return (
        <ModalEdit
            {...{ show, onClose }}
            onSuccessClose={false}
            onClose={() => {
                if (!blockModal) {
                    onClose();
                } else {
                    Alert.warning(formatMessage(m.provideTeams), 2000);
                }
            }}
            onSuccess={() => onSubmit()}
            successButton={formatMessage(m.send)}
            // successButtonProps={{
            //     disabled:
            //         !checkFormError(errorValue)
            // }}
            title={formatMessage(m.accountContacts)}
            width={maxWidth}
        >
            {contacts.map((contact) => {

                return <Form
                    key={contact.id}
                    ref={ref => updateRefsData(ref, contact.id)}
                    model={formProfileContactModel}
                    onChange={(formValues) => {
                        const checkedData = formProfileContactModel.check(formValues);

                        if (checkedData) {
                            updateFormValues(formValues, contact.id);
                        }
                    }}
                    // formValue={contact}
                    formDefaultValue={contact}
                >
                    <FilterSectionFlexboxGrid controlable={!isMobile} style={{ marginBottom: 10 }}>
                        <StyledItemFitWidth isMobile={isMobile} controlable={!isMobile}>
                            <Form.Control
                                name="person"
                                type="text"
                                disabled
                                style={{ width: 160, marginRight: 10 }}
                                placeholder={"Name"}
                                className="edit-profile-contact-input"
                            />
                        </StyledItemFitWidth>
                        <StyledItemFitWidth isMobile={isMobile} controlable={!isMobile}>
                            <Whisper
                                trigger="hover"
                                placement="auto"
                                speaker={<Tooltip>{contact?.email}</Tooltip>}
                            >
                                <Form.Control
                                    name="email"
                                    type="text"
                                    disabled
                                    style={{ width: 220, marginRight: 10 }}
                                    placeholder={"Email"}
                                    className="edit-profile-contact-input"
                                />
                            </Whisper>
                        </StyledItemFitWidth>
                        <StyledItemFitWidth isMobile={isMobile} controlable={!isMobile}>
                            <Whisper
                                trigger="hover"
                                speaker={<Tooltip>{formatMessage(m.sendRequestAboutChangingContactNameOrEmail)}</Tooltip>}
                            >
                                <ButtonPrimary
                                    onClick={() => {
                                        setShowLetterModal(true);
                                        setLetterModalData({
                                            id: contact.id,
                                            person: contact.person,
                                            email: contact.email
                                        });
                                    }}
                                    // appearance="link"
                                    style={{ width: 200 }}
                                    color="violet"
                                    buttonStyle="primary"
                                    disabled={loading}
                                >
                                    <Icon icon="envelope" />
                                    <span style={{ marginLeft: 10 }}>{formatMessage(m.changeNameOrEmail)}</span>
                                </ButtonPrimary>
                            </Whisper>
                        </StyledItemFitWidth>
                    </FilterSectionFlexboxGrid>
                    <FilterSectionFlexboxGrid controlable={!isMobile} style={{ marginBottom: 20 }}>
                        <StyledItemFitWidth isMobile={isMobile} controlable={!isMobile}>
                            <Form.Control
                                name="phone"
                                type="text"
                                errorPlacement="auto"
                                style={{ width: 130, marginRight: 10 }}
                                placeholder={formatMessage(m.phone)}
                                className="edit-profile-contact-input"
                            />
                        </StyledItemFitWidth>
                        <StyledItemFitWidth isMobile={isMobile} controlable={!isMobile}>
                            <Form.Control
                                name="telegram_username"
                                type="text"
                                errorPlacement="bottomEnd"
                                style={{ width: 150, marginRight: 10 }}
                                placeholder={formatMessage(m.telegram)}
                                className="edit-profile-contact-input"
                            />
                        </StyledItemFitWidth>
                        <StyledItemFitWidth isMobile={isMobile} controlable={!isMobile}>
                            <Form.Control
                                name="teams_email"
                                type="text"
                                errorPlacement="bottomEnd"
                                style={{ width: 180, marginRight: 10 }}
                                placeholder={formatMessage(m.teamsEmail)}
                                className="edit-profile-contact-input"
                            />
                        </StyledItemFitWidth>
                        <StyledItemFitWidth isMobile={isMobile} controlable={!isMobile}>
                            <Form.Control
                                name="teams_link"
                                type="text"
                                errorPlacement="bottomEnd"
                                style={{ width: 160 }}
                                placeholder={formatMessage(m.teamsLink)}
                                className="edit-profile-contact-input"
                            />
                        </StyledItemFitWidth>
                    </FilterSectionFlexboxGrid>
                </Form>
            })}
            {showLetterModal && <ModalEditProfileContactsLetter
                show={showLetterModal}
                onClose={() => {
                    setShowLetterModal(false);
                }}
                setLoading={setLoading}
                defaultValue={letterModalData}
                onSuccess={modifyAccountContactRequest}
            />}
        </ModalEdit>
    );
};


export default withTranslation(ModalEditProfileContacts);

// const StyledFieldWrapper = styled(FlexboxGrid.Item)`
//     && {
//         margin-right: 10px;
//     }
// `;
